import { useState, useEffect, useMemo } from "react";
import { Modal, Form, Card, Badge, InputGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useNotification } from "hooks/useNotification";
import { FinancialStatementService } from "services/financialStatement";
import { ExaminerRewardsService } from "services/examinerRewards";
import { useCookieContext } from "contexts";

type Props = {
  isOpen: boolean;
  onClose: (refetch?: boolean) => void;
};

export default function CreateFinancialStatementModal({ isOpen, onClose }: Props) {
  const { t } = useTranslation(["FinancialStatement", "Common"]);
  const { showNotification } = useNotification();
  const { isAr } = useCookieContext();

  const [examinerRewardIds, setExaminerRewardIds] = useState<number[]>([]);
  const [researchPaymentIds, setResearchPaymentIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [pendingExaminerRewards, setPendingExaminerRewards] = useState<any[]>([]);
  const [pendingResearchPayments, setPendingResearchPayments] = useState<any[]>([]);
  const [searchExaminerRewards, setSearchExaminerRewards] = useState("");
  const [searchResearchPayments, setSearchResearchPayments] = useState("");

  useEffect(() => {
    if (isOpen) {
      fetchPendingItems();
    }
  }, [isOpen]);

  const fetchPendingItems = async () => {
    try {
      setFetchingData(true);
      // Fetch pending examiner rewards
      const examinerRewardsResponse = await ExaminerRewardsService.getAllExaminersRewards({
        pageIndex: 1,
        pageSize: 1000,
        search: "",
        sort: [],
        filter: [{ field: "status", value: "Pending", operator: "contains" }],
      });
      setPendingExaminerRewards(examinerRewardsResponse.data.result || []);

      // TODO: Fetch pending research payments when service is available
      setPendingResearchPayments([]);
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setFetchingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (examinerRewardIds.length === 0 && researchPaymentIds.length === 0) {
      showNotification({
        type: "error",
        description: t("noItemsSelected"),
      });
      return;
    }

    try {
      setLoading(true);
      const response = await FinancialStatementService.createFinancialStatement({
        examinerRewardIds,
        researchPaymentIds,
      });

      if (!response.hasError) {
        showNotification({
          type: "success",
          description: t("financialStatementCreatedSuccessfully"),
        });
        onClose(true);
      } else {
        showNotification({
          type: "error",
          description: response.description || t("Common:internalServerError"),
        });
      }
    } catch (error) {
      showNotification({
        type: "error",
        description: t("Common:internalServerError"),
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExaminerRewardToggle = (id: number) => {
    setExaminerRewardIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleResearchPaymentToggle = (id: number) => {
    setResearchPaymentIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleSelectAllExaminerRewards = () => {
    if (examinerRewardIds.length === filteredExaminerRewards.length) {
      setExaminerRewardIds([]);
    } else {
      setExaminerRewardIds(filteredExaminerRewards.map(r => r.id));
    }
  };

  const handleSelectAllResearchPayments = () => {
    if (researchPaymentIds.length === filteredResearchPayments.length) {
      setResearchPaymentIds([]);
    } else {
      setResearchPaymentIds(filteredResearchPayments.map(p => p.id));
    }
  };

  const filteredExaminerRewards = useMemo(() => {
    if (!searchExaminerRewards.trim()) return pendingExaminerRewards;
    const search = searchExaminerRewards.toLowerCase();
    return pendingExaminerRewards.filter(reward =>
      (reward.examiner?.arabicName?.toLowerCase().includes(search)) ||
      (reward.examiner?.englishName?.toLowerCase().includes(search)) ||
      (reward.orderNumber?.toLowerCase().includes(search))
    );
  }, [pendingExaminerRewards, searchExaminerRewards]);

  const filteredResearchPayments = useMemo(() => {
    if (!searchResearchPayments.trim()) return pendingResearchPayments;
    const search = searchResearchPayments.toLowerCase();
    return pendingResearchPayments.filter(payment =>
      payment.programOrderId?.toString().includes(search)
    );
  }, [pendingResearchPayments, searchResearchPayments]);

  const totalSelectedAmount = useMemo(() => {
    const rewardsTotal = pendingExaminerRewards
      .filter(r => examinerRewardIds.includes(r.id))
      .reduce((sum, r) => sum + (r.value || 0), 0);
    const paymentsTotal = pendingResearchPayments
      .filter(p => researchPaymentIds.includes(p.id))
      .reduce((sum, p) => sum + (p.amount || 0), 0);
    return rewardsTotal + paymentsTotal;
  }, [pendingExaminerRewards, pendingResearchPayments, examinerRewardIds, researchPaymentIds]);

  return (
    <Modal show={isOpen} onHide={() => onClose()} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <i className="icon-plus mx-2"></i>
          {t("createFinancialStatement")}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {fetchingData ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("loading")}</span>
              </div>
            </div>
          ) : (
            <>
              {/* Statement Information Section */}
              <Card className="mb-3">
                <Card.Header className="bg-light">
                  <h6 className="mb-0">
                    <i className="icon-info mx-2"></i>
                    {t("statementInformation")}
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="alert alert-info mb-3">
                    <i className="icon-info-circle mx-2"></i>
                    {t("serialNumberAutoGenerated")}
                  </div>

                  {/* Summary */}
                  <div className="d-flex justify-content-between align-items-center p-3 bg-light rounded">
                    <div>
                      <strong>{t("selectedItems")}:</strong>{" "}
                      <Badge bg="primary" className="ms-2">
                        {examinerRewardIds.length + researchPaymentIds.length} {t("itemsCount")}
                      </Badge>
                    </div>
                    <div>
                      <strong>{t("totalSelectedAmount")}:</strong>{" "}
                      <span className="text-primary fs-5 fw-bold ms-2">
                        {totalSelectedAmount.toFixed(2)} {t("riyal")}
                      </span>
                    </div>
                  </div>
                </Card.Body>
              </Card>

              {/* Examiner Rewards Section */}
              <Card className="mb-3">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <i className="icon-users mx-2"></i>
                    {t("pendingExaminerRewards")}{" "}
                    <Badge bg="info">{filteredExaminerRewards.length}</Badge>
                  </h6>
                  {filteredExaminerRewards.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleSelectAllExaminerRewards}
                    >
                      {examinerRewardIds.length === filteredExaminerRewards.length
                        ? t("deselectAll")
                        : t("selectAll")}
                    </button>
                  )}
                </Card.Header>
                <Card.Body>
                  {filteredExaminerRewards.length > 0 && (
                    <InputGroup className="mb-3">
                      <InputGroup.Text>
                        <i className="icon-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder={t("searchItems")}
                        value={searchExaminerRewards}
                        onChange={(e) => setSearchExaminerRewards(e.target.value)}
                      />
                    </InputGroup>
                  )}

                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {filteredExaminerRewards.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        <i className="icon-info-circle fs-2"></i>
                        <p className="mt-2 mb-0">{t("noAvailableItems")}</p>
                      </div>
                    ) : (
                      <div className="row g-2">
                        {filteredExaminerRewards.map((reward) => (
                          <div key={reward.id} className="col-md-6">
                            <div
                              className={`border rounded p-3 cursor-pointer ${
                                examinerRewardIds.includes(reward.id)
                                  ? "border-primary bg-primary bg-opacity-10"
                                  : "border-secondary"
                              }`}
                              onClick={() => handleExaminerRewardToggle(reward.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <Form.Check
                                type="checkbox"
                                id={`examiner-reward-${reward.id}`}
                                checked={examinerRewardIds.includes(reward.id)}
                                onChange={() => handleExaminerRewardToggle(reward.id)}
                                label={
                                  <div>
                                    <div className="fw-bold">
                                      {isAr ? reward.examiner?.arabicName : reward.examiner?.englishName}
                                    </div>
                                    <div className="small text-muted">
                                      {t("orderNumber")}: {reward.orderNumber}
                                    </div>
                                    <div className="text-primary fw-bold mt-1">
                                      {reward.value} {t("riyal")}
                                    </div>
                                  </div>
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>

              {/* Research Payments Section */}
              <Card>
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h6 className="mb-0">
                    <i className="icon-money mx-2"></i>
                    {t("pendingResearchPayments")}{" "}
                    <Badge bg="info">{filteredResearchPayments.length}</Badge>
                  </h6>
                  {filteredResearchPayments.length > 0 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleSelectAllResearchPayments}
                    >
                      {researchPaymentIds.length === filteredResearchPayments.length
                        ? t("deselectAll")
                        : t("selectAll")}
                    </button>
                  )}
                </Card.Header>
                <Card.Body>
                  {filteredResearchPayments.length > 0 && (
                    <InputGroup className="mb-3">
                      <InputGroup.Text>
                        <i className="icon-search"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder={t("searchItems")}
                        value={searchResearchPayments}
                        onChange={(e) => setSearchResearchPayments(e.target.value)}
                      />
                    </InputGroup>
                  )}

                  <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {filteredResearchPayments.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        <i className="icon-info-circle fs-2"></i>
                        <p className="mt-2 mb-0">{t("noAvailableItems")}</p>
                      </div>
                    ) : (
                      <div className="row g-2">
                        {filteredResearchPayments.map((payment) => (
                          <div key={payment.id} className="col-md-6">
                            <div
                              className={`border rounded p-3 cursor-pointer ${
                                researchPaymentIds.includes(payment.id)
                                  ? "border-primary bg-primary bg-opacity-10"
                                  : "border-secondary"
                              }`}
                              onClick={() => handleResearchPaymentToggle(payment.id)}
                              style={{ cursor: "pointer" }}
                            >
                              <Form.Check
                                type="checkbox"
                                id={`research-payment-${payment.id}`}
                                checked={researchPaymentIds.includes(payment.id)}
                                onChange={() => handleResearchPaymentToggle(payment.id)}
                                label={
                                  <div>
                                    <div className="fw-bold">
                                      {t("orderNumber")}: {payment.programOrderId}
                                    </div>
                                    <div className="text-primary fw-bold mt-1">
                                      {payment.amount} {t("riyal")}
                                    </div>
                                  </div>
                                }
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => onClose()}
            disabled={loading || fetchingData}
          >
            <i className="icon-close mx-2"></i>
            {t("cancel")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || fetchingData}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mx-2" role="status" aria-hidden="true"></span>
                {t("loading")}
              </>
            ) : (
              <>
                <i className="icon-check mx-2"></i>
                {t("submit")}
              </>
            )}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
